import * as React from "react";
import type { LucideIcon, LucideProps } from "lucide-react";

// Centers of the 3×3 grid and the size of each square dot (24-unit viewBox).
const DOT_COORDS = [6, 12, 18];
const DOT_SIZE = 4;

/**
 * GridDots — a 3×3 grid of small rounded squares ("app launcher" / "More").
 *
 * Lucide has no dotted-grid icon, so this is authored to behave exactly like a
 * Lucide icon: it accepts `LucideProps` and the dots use `fill="currentColor"`
 * so they inherit the same ambient-color treatment as the stroke-based Lucide
 * icons when rendered through `<Icon>`. Registered in the icon registry as
 * `"grid-dots"`.
 *
 * Squares (not circles) are used deliberately: at the small rendered size their
 * straight, axis-aligned edges stay crisp, whereas anti-aliased circles blur.
 * `shape-rendering: crispEdges` disables anti-aliasing to keep them pixel-sharp.
 */
function GridDotsBase({
  size = 24,
  color = "currentColor",
  ref,
  ...props
}: LucideProps & { ref?: React.Ref<SVGSVGElement> }) {
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      {...(props as React.SVGProps<SVGSVGElement>)}
    >
      {DOT_COORDS.flatMap((cy) =>
        DOT_COORDS.map((cx) => (
          <rect
            key={`${cx}-${cy}`}
            x={cx - DOT_SIZE / 2}
            y={cy - DOT_SIZE / 2}
            width={DOT_SIZE}
            height={DOT_SIZE}
            fill={color as string}
            shapeRendering="crispEdges"
          />
        )),
      )}
    </svg>
  );
}
GridDotsBase.displayName = "GridDots";

export const GridDots = GridDotsBase as unknown as LucideIcon;

/**
 * GoogleLogo — the multi-color Google "G", for the "Continue with Google"
 * button. A brand mark carries its own fixed palette by definition, so the
 * four fills are hard-coded brand hexes rather than theme tokens, and the
 * ambient `color`/stroke treatment is deliberately ignored — the mark must
 * render identically in both themes (Google's branding requirement).
 */
// A third-party mark sits deliberately outside tokens.css: the theme
// generator retunes every token against our surfaces, and Google's palette
// may never be retuned or flipped by a theme.
/* eslint-disable no-restricted-syntax -- brand mark carries its own fixed palette */
const GOOGLE_BRAND = {
  blue: "#4285F4",
  green: "#34A853",
  yellow: "#FBBC05",
  red: "#EA4335",
} as const;
/* eslint-enable no-restricted-syntax */

function GoogleLogoBase({
  size = 24,
  ref,
  ...props
}: LucideProps & { ref?: React.Ref<SVGSVGElement> }) {
  const { color: _color, strokeWidth: _strokeWidth, ...rest } = props;
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      {...(rest as React.SVGProps<SVGSVGElement>)}
    >
      <path
        fill={GOOGLE_BRAND.blue}
        d="M23.52 12.273c0-.851-.076-1.669-.218-2.455H12v4.642h6.458a5.52 5.52 0 0 1-2.394 3.622v3.011h3.878c2.268-2.089 3.578-5.165 3.578-8.82Z"
      />
      <path
        fill={GOOGLE_BRAND.green}
        d="M12 24c3.24 0 5.956-1.075 7.941-2.907l-3.877-3.011c-1.075.72-2.449 1.145-4.064 1.145-3.125 0-5.771-2.111-6.715-4.947H1.276v3.109A11.995 11.995 0 0 0 12 24Z"
      />
      <path
        fill={GOOGLE_BRAND.yellow}
        d="M5.285 14.28A7.213 7.213 0 0 1 4.909 12c0-.791.136-1.56.376-2.28V6.611H1.276A11.995 11.995 0 0 0 0 12c0 1.936.464 3.769 1.276 5.389l4.009-3.109Z"
      />
      <path
        fill={GOOGLE_BRAND.red}
        d="M12 4.773c1.762 0 3.344.605 4.587 1.794l3.442-3.442C17.951 1.19 15.235 0 12 0 7.31 0 3.253 2.69 1.276 6.611l4.009 3.109C6.229 6.884 8.875 4.773 12 4.773Z"
      />
    </svg>
  );
}
GoogleLogoBase.displayName = "GoogleLogo";

export const GoogleLogo = GoogleLogoBase as unknown as LucideIcon;
